import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { plural, t } from '@lingui/macro';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import KDSTagInput, {
  TagItem,
} from '@decisiv/ui-components/lib/components/TagInput';
import Message from '@decisiv/ui-components/lib/components/Message';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import QuestionCircle from '@decisiv/iconix/lib/components/QuestionCircle';
import Times from '@decisiv/iconix/lib/components/Times';

import { transformTags } from '../utils/transformTags';
import { MAX_VINS } from './schema';
import { TagInputWrapper } from './styles';

const getTagText = (tag: TagItem): string =>
  typeof tag === 'string' ? tag : tag.text;

const dedupeNewTags = (
  existingTexts: Set<string>,
  newTags: TagItem[],
): { deduped: TagItem[]; duplicateCount: number } =>
  newTags.reduce(
    (acc, tag) => {
      const text = getTagText(tag);
      if (acc.seen.has(text)) {
        return { ...acc, duplicateCount: acc.duplicateCount + 1 };
      }
      acc.seen.add(text);
      return { ...acc, deduped: [...acc.deduped, tag] };
    },
    {
      seen: new Set<string>(existingTexts),
      deduped: [] as TagItem[],
      duplicateCount: 0,
    },
  );

const VinTagInput = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const [clearKey, setClearKey] = React.useState(0);
  const [duplicateCount, setDuplicateCount] = React.useState(0);

  // Only the first MAX_VINS entries are accepted in the manual flow. Anything
  // beyond that is dropped and the user is pointed to the Bulk Add wizard.
  const capToLimit = (tags: TagItem[]): TagItem[] => tags.slice(0, MAX_VINS);

  return (
    <Controller
      name="vins"
      control={control}
      render={({ field: { onChange, value } }) => {
        const existing: TagItem[] = value ?? [];
        const existingTexts = new Set<string>(existing.map(getTagText));

        const commitInput = (inputValue: string) => {
          const newTags = transformTags([inputValue]);
          const { deduped, duplicateCount: removedDuplicateCount } =
            dedupeNewTags(existingTexts, newTags);

          if (removedDuplicateCount > 0) {
            setDuplicateCount(removedDuplicateCount);
          }

          /* istanbul ignore next */
          if (removedDuplicateCount === 0 && deduped.length > 0) {
            setDuplicateCount(0);
          }

          if (deduped.length > 0)
            onChange(capToLimit([...existing, ...deduped]));
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key !== 'Enter' && e.key !== 'Tab') return;

          const input = e.target as HTMLInputElement;
          const inputValue = input.value.trim();

          if (e.key === 'Enter') e.preventDefault();
          if (!inputValue) return;
          if (e.key === 'Tab') e.preventDefault();

          commitInput(inputValue);
          input.value = '';
        };

        const handleTagChange = (tags: TagItem[]) => {
          if (tags.length < existing.length) {
            onChange(tags);
            return;
          }

          const transformedTags = transformTags(tags);
          const newTags = transformedTags.slice(existing.length);
          const { deduped, duplicateCount: removedDuplicateCount } =
            dedupeNewTags(existingTexts, newTags);

          if (removedDuplicateCount > 0) {
            setDuplicateCount(removedDuplicateCount);
          }

          /* istanbul ignore next */
          if (removedDuplicateCount === 0 && deduped.length > 0) {
            setDuplicateCount(0);
          }

          onChange(capToLimit([...existing, ...deduped]));
        };

        const helpMessage = t`Max amount ${MAX_VINS} VINs.`;
        const atLimit = existing.length >= MAX_VINS;

        return (
          <Flex flexDirection="column">
            {duplicateCount > 0 && (
              <Flex marginBottom={0.5}>
                <Alert
                  title={plural(duplicateCount, {
                    one: '# duplicate VIN was removed.',
                    other: '# duplicate VINs were removed.',
                  })}
                  intent="warning"
                  dismissable
                  onDismissClick={() => setDuplicateCount(0)}
                />
              </Flex>
            )}
            <TagInputWrapper className={atLimit ? 'at-limit' : undefined}>
              <KDSTagInput
                key={clearKey}
                tags={value}
                onChange={handleTagChange}
                onKeyDown={handleKeyDown}
                warningMessage={errors.vins?.message?.toString()}
                label={t`VIN`}
                maxTags={MAX_VINS}
                options={[]}
                required
                showMaxTagsCount
                withWarningIntent={atLimit}
              />
            </TagInputWrapper>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              marginTop={0.5}
            >
              <Flex flexDirection="column">
                {atLimit && (
                  <Flex marginBottom={0.5}>
                    <Message intent="warning" size="small">
                      {t`You have reached the max amount of VINs (${MAX_VINS})`}
                    </Message>
                  </Flex>
                )}
                <Flex alignItems="center">
                  <QuestionCircle size="small" color="alaskanHusky" />
                  <P size="small" shade={1} marginLeft={0.5}>
                    {helpMessage}
                  </P>
                </Flex>
              </Flex>
              <Button
                text={t`Clear`}
                size="small"
                variant="ghost"
                icon={Times}
                type="button"
                disabled={existing.length === 0}
                onClick={() => {
                  onChange([]);
                  setClearKey((k) => k + 1);
                }}
              />
            </Flex>
          </Flex>
        );
      }}
    />
  );
};

export default VinTagInput;
